<template>
  <div>
    <vs-alert
      :active="!protocol && !loading"
      color="danger"
      icon="error_outline"
    >
      <span>Protocol ID: {{ protocolId }} not found!</span>
    </vs-alert>
    <vs-progress v-if="loading" indeterminate color="primary"></vs-progress>
    <div v-if="protocol && !loading">
      <div id="main-container">
        <!-- Header -->
        <vx-card class="mb-5">
          <vs-row vs-justify="space-between" vs-align="center">
            <div>
              <h2>{{ protocol.name_internal }} (ID: {{ protocol.id }})</h2>
            </div>
            <div>
              <vs-button color="danger" type="border" class="mr-2" @click="confirmDelete" :disabled="loading">Delete</vs-button>
              <vs-button color="primary" @click="save" :disabled="loading">Save</vs-button>
            </div>
          </vs-row>
        </vx-card>

        <!-- Errors -->
        <vs-alert
          :active="!!errors.length"
          color="danger"
          icon="error_outline"
          class="mb-5"
        >
          <div v-for="(error, index) in errors" :key="`p_error_${index}`">{{ error }}</div>
        </vs-alert>

        <!-- General -->
        <vx-card title="General" class="mb-5">
          <div class="vx-row">
            <div class="vx-col lg:w-1/2 w-full mb-4">
              <vs-input
                class="w-full"
                label="Name (Internal)"
                v-model="workingProtocol.name_internal"
              />
            </div>
            <div class="vx-col lg:w-1/2 w-full mb-4">
              <label class="vs-input--label">Category</label>
              <vs-select class="w-full" v-model="workingProtocol.category_id">
                <vs-select-item
                  v-for="cat in categories"
                  :key="cat.id"
                  :value="cat.id"
                  :text="cat.name_internal"
                />
              </vs-select>
            </div>
          </div>
          <div class="vx-row">
            <div class="vx-col w-full mb-2">
              <div class="flex items-center justify-between">
                <label class="vs-input--label">Filter Products by Company</label>
                <div>
                  <vs-button
                    size="small"
                    type="flat"
                    @click="setAllCompanies(true)"
                  >Select All</vs-button>
                  <vs-button
                    size="small"
                    type="flat"
                    @click="setAllCompanies(false)"
                  >Deselect All</vs-button>
                </div>
              </div>
              <div class="flex flex-wrap">
                <vs-checkbox
                  v-for="company in companies"
                  :key="company.company_id"
                  v-model="company.isSelected"
                  class="mr-4 mb-2"
                >
                  {{ company.name }}
                </vs-checkbox>
              </div>
            </div>
          </div>
          <div class="vx-row">
            <div class="vx-col w-full">
              <label class="vs-input--label">{{ selectedProductIds.length }} Products</label>
              <v-select
                multiple
                label="_name"
                :value="selectedProducts"
                :reduce="product => product.product_id"
                :options="unSelectedProducts"
                @input="productSelectionChanged"
              />
            </div>
          </div>
        </vx-card>

        <!-- Translations + Content side-by-side -->
        <div class="vx-row">
          <div class="vx-col lg:w-1/4 w-full mb-5">
            <vx-card title="Translations">
              <p class="text-sm mb-3 opacity-75">Clear both fields and save to delete an existing translation.</p>
              <template v-for="(translation, index) in workingTranslations">
                <div :key="`lang-${translation.language_id}`" class="py-2 my-2">
                  <vs-input
                    class="w-full mb-2"
                    :label="`${translation.language_name} — Name`"
                    v-model="workingTranslations[index].name"
                  />
                  <label class="vs-input--label">{{ translation.language_name }} — Description</label>
                  <vs-textarea
                    class="w-full"
                    v-model="workingTranslations[index].description"
                  />
                </div>
              </template>
            </vx-card>
          </div>
          <div class="vx-col lg:w-3/4 w-full mb-5">
            <vx-card title="Content">
          <p class="text-sm mb-4 opacity-75">
            Manage body content for each language and usage type combination. Content supports markdown.
          </p>

          <!-- Language tabs -->
          <vs-tabs v-if="languages.length">
            <vs-tab v-for="language in languages" :key="`tab-${language.language_id}`" :label="language.name">
              <div class="pt-4">
                <div v-for="usageType in usageTypes" :key="`content-${language.language_id}-${usageType.id}`" class="mb-6">
                  <label class="vs-input--label font-semibold">{{ usageType.name_internal }}</label>
                  <div class="content-entry">
                    <vs-textarea
                      class="w-full"
                      v-model="workingContents[contentKey(language.language_id, usageType.id)]"
                    />
                    <div v-if="hasExistingContent(language.language_id, usageType.id)" class="mt-1">
                      <vs-button
                        size="small"
                        color="danger"
                        type="flat"
                        @click="clearContent(language.language_id, usageType.id)"
                      >Clear (will delete on save)</vs-button>
                    </div>
                  </div>
                </div>
              </div>
            </vs-tab>
          </vs-tabs>
            </vx-card>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import api from "@/http/api";

export default {
  data() {
    return {
      loading: false,
      protocol: null,
      protocolId: parseInt(this.$route.params.id),
      languages: [],
      categories: [],
      usageTypes: [],
      companies: [],
      defaultCompanyIds: [1, 7],
      allProducts: [],
      selectedProductIds: [],
      workingProtocol: { name_internal: '', category_id: null },
      workingTranslations: [],
      workingContents: {},
      originalContents: {},
      contentIdMap: {},
      errors: []
    }
  },
  computed: {
    selectedProducts() {
      return this.allProducts.filter(p => this.selectedProductIds.includes(p.product_id));
    },
    unSelectedProducts() {
      const selectedCompanySet = new Set(this.selectedCompanyIds);
      return this.allProducts.filter(p => {
        if (this.selectedProductIds.includes(p.product_id)) return false;
        return p._companyIds.some(id => selectedCompanySet.has(id));
      });
    },
    selectedCompanyIds() {
      return this.companies.filter(c => c.isSelected).map(c => c.company_id);
    }
  },
  mounted() {
    this.fetchData();
  },
  methods: {
    contentKey(languageId, usageTypeId) {
      return `${languageId}_${usageTypeId}`;
    },

    async fetchData() {
      this.loading = true;

      const previouslySelectedCompanyIds = this.companies.length
        ? new Set(this.selectedCompanyIds)
        : new Set(this.defaultCompanyIds);

      const [protocol, languages, categories, usageTypes, companies, protocolProducts] = await Promise.all([
        api.getProtocol(this.protocolId),
        api.fetchAllLanguages(),
        api.getProtocolCategories(),
        api.getProtocolUsageTypes(),
        api.fetchActiveCompanies(),
        api.getProtocolProducts(this.protocolId)
      ]);

      this.protocol = protocol;
      this.languages = languages;
      this.categories = categories;
      this.usageTypes = usageTypes;

      this.companies = (companies || []).map(c => {
        c.isSelected = previouslySelectedCompanyIds.has(c.company_id);
        return c;
      });

      this.selectedProductIds = protocolProducts.map(pp => pp.product_id);

      await this.fetchAllProducts();

      if (this.protocol) {
        this.buildWorkingProtocol();
        this.buildWorkingTranslations();
        this.buildWorkingContents();
      }

      this.loading = false;
    },

    setAllCompanies(selected) {
      this.companies.forEach(c => { c.isSelected = selected; });
    },

    async fetchAllProducts() {
      if (!this.companies.length) {
        this.allProducts = [];
        return;
      }

      const results = await Promise.all(
        this.companies.map(c =>
          api.fetchAllProductNames(c.company_id, true).then(r => ({
            companyId: c.company_id,
            products: (r && r.products) || []
          }))
        )
      );

      const byId = new Map();
      for (const { companyId, products } of results) {
        for (const p of products) {
          const existing = byId.get(p.product_id);
          if (existing) {
            existing._companyIds.push(companyId);
          } else {
            p._name = `${p.product_id} - ${p.company_name} - ${p.product_name}`;
            p._companyIds = [companyId];
            byId.set(p.product_id, p);
          }
        }
      }
      this.allProducts = Array.from(byId.values());
    },

    buildWorkingProtocol() {
      this.workingProtocol = {
        name_internal: this.protocol.name_internal || '',
        category_id: this.protocol.category_id || null
      };
    },

    buildWorkingTranslations() {
      const existing = this.protocol.translations || [];
      this.workingTranslations = this.languages.map((language) => {
        const match = existing.find(t => t.language_id === language.language_id);
        return {
          language_id: language.language_id,
          language_name: language.name,
          name: match ? match.name : '',
          original_name: match ? match.name : '',
          description: match ? (match.description || '') : '',
          original_description: match ? (match.description || '') : '',
          existed: !!match
        };
      });
    },

    buildWorkingContents() {
      const existing = this.protocol.contents || [];
      const contents = {};
      this.originalContents = {};
      this.contentIdMap = {};

      for (const content of existing) {
        const key = this.contentKey(content.language_id, content.usage_type_id);
        contents[key] = content.body || '';
        this.originalContents[key] = content.body || '';
        this.contentIdMap[key] = content.id;
      }

      for (const language of this.languages) {
        for (const usageType of this.usageTypes) {
          const key = this.contentKey(language.language_id, usageType.id);
          if (!(key in contents)) {
            contents[key] = '';
          }
          if (!(key in this.originalContents)) {
            this.originalContents[key] = '';
          }
        }
      }

      this.workingContents = contents;
    },

    hasExistingContent(languageId, usageTypeId) {
      const key = this.contentKey(languageId, usageTypeId);
      return key in this.contentIdMap;
    },

    clearContent(languageId, usageTypeId) {
      const key = this.contentKey(languageId, usageTypeId);
      this.$set(this.workingContents, key, '');
    },

    async productSelectionChanged(newSelectedIds) {
      if (newSelectedIds.length === this.selectedProductIds.length) return;
      const added = newSelectedIds.length > this.selectedProductIds.length;

      let success = false;
      if (added) {
        const addedId = newSelectedIds.find(id => !this.selectedProductIds.includes(id));
        success = await api.addProtocolProduct(this.protocolId, addedId);
      } else {
        const removedId = this.selectedProductIds.find(id => !newSelectedIds.includes(id));
        success = await api.removeProtocolProduct(this.protocolId, removedId);
      }

      this.$vs.notify({
        title: success ? 'Success' : 'Error',
        text: added ? `Product${success ? '' : ' NOT'} added.` : `Product${success ? '' : ' NOT'} removed.`,
        color: success ? 'success' : 'danger',
        icon: success ? 'check_circle_outline' : 'error_outline'
      });

      this.selectedProductIds = newSelectedIds;
    },

    async save() {
      this.errors = [];
      this.loading = true;

      await this.updateGeneral();
      await this.updateTranslations();
      await this.updateContents();
      await this.fetchData();

      if (!this.errors.length) {
        this.$vs.notify({
          title: 'Success',
          text: 'Saved Successfully!',
          color: 'success',
          icon: 'check_circle_outline'
        });
      }

      this.loading = false;
    },

    async updateGeneral() {
      const trimmedName = (this.workingProtocol.name_internal || '').trim();
      const categoryChanged = this.workingProtocol.category_id !== this.protocol.category_id;
      const nameChanged = trimmedName !== (this.protocol.name_internal || '');

      if (!nameChanged && !categoryChanged) return;

      if (!trimmedName) {
        this.errors.push('Name (Internal) cannot be empty.');
        return;
      }

      const data = {};
      if (nameChanged) data.name_internal = trimmedName;
      if (categoryChanged) data.category_id = this.workingProtocol.category_id;

      const result = await api.updateProtocol(this.protocolId, data);
      if (!result) this.errors.push('The General section was NOT saved successfully.');
    },

    async updateTranslations() {
      const ops = this.workingTranslations.map((t) => {
        const trimmedName = (t.name || '').trim();
        const trimmedDesc = (t.description || '').trim();
        const hasContent = trimmedName || trimmedDesc;
        const nameChanged = trimmedName !== t.original_name;
        const descChanged = trimmedDesc !== t.original_description;

        if (!t.existed && hasContent) {
          return api.createProtocolTranslation(this.protocolId, {
            language_id: t.language_id, name: trimmedName, description: trimmedDesc
          });
        }
        if (t.existed && !hasContent) {
          return api.deleteProtocolTranslation(this.protocolId, t.language_id);
        }
        if (t.existed && (nameChanged || descChanged)) {
          return api.updateProtocolTranslation(this.protocolId, t.language_id, {
            name: trimmedName, description: trimmedDesc
          });
        }
        return Promise.resolve(true);
      });

      const results = await Promise.all(ops);
      if (results.some(r => r === false || r === null)) {
        this.errors.push('Some translations were NOT saved successfully.');
      }
    },

    async updateContents() {
      const ops = [];

      for (const language of this.languages) {
        for (const usageType of this.usageTypes) {
          const key = this.contentKey(language.language_id, usageType.id);
          const current = (this.workingContents[key] || '').trim();
          const original = (this.originalContents[key] || '').trim();
          const existingId = this.contentIdMap[key];

          if (!existingId && current) {
            ops.push(api.createProtocolContent(this.protocolId, {
              language_id: language.language_id,
              usage_type_id: usageType.id,
              body: current
            }));
          } else if (existingId && !current) {
            ops.push(api.deleteProtocolContent(this.protocolId, existingId));
          } else if (existingId && current !== original) {
            ops.push(api.updateProtocolContent(this.protocolId, existingId, {
              language_id: language.language_id,
              usage_type_id: usageType.id,
              body: current
            }));
          } else {
            ops.push(Promise.resolve(true));
          }
        }
      }

      const results = await Promise.all(ops);
      if (results.some(r => r === false || r === null)) {
        this.errors.push('Some content entries were NOT saved successfully.');
      }
    },

    confirmDelete() {
      this.$vs.dialog({
        type: 'confirm',
        color: 'danger',
        title: 'Delete Protocol',
        text: `Are you sure you want to delete "${this.protocol.name_internal}"? This cannot be undone.`,
        acceptText: 'Delete',
        cancelText: 'Cancel',
        accept: this.deleteProtocol
      });
    },

    async deleteProtocol() {
      this.loading = true;
      const ok = await api.deleteProtocol(this.protocolId);
      this.loading = false;

      if (!ok) {
        this.$vs.notify({
          title: 'Error',
          text: 'Failed to delete protocol.',
          color: 'danger',
          icon: 'error_outline'
        });
        return;
      }

      this.$vs.notify({
        title: 'Deleted',
        text: 'Protocol deleted.',
        color: 'success',
        icon: 'check_circle_outline'
      });
      this.$router.push('/content/protocols').catch(() => {});
    }
  }
}
</script>
